// Offline-mode data caches (spec §2): patient roster + encounter list, stored
// in the shared LucasAI IndexedDB, hard-partitioned per user+org scope so a
// shared device can never leak one user's rows to another. Everything here is
// best-effort: failures resolve false/null and the app behaves as "no cache".
// PHI note: rows are protected by OS device encryption + device unlock (the
// posture approved in the spec). NO token/credential material belongs here.
import { awaitTransaction as awaitTx, openLucasDb, promisifyRequest as promisify } from "./lucasDb";
import { buildPatientSearchText, parseChatSearchTerm, patientMatchesSearch } from "./chatSearch";

export const ROSTER_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const CHAT_WINDOW_MAX_COUNT = 200;
const CHAT_WINDOW_MAX_AGE_DAYS = 30;

// Monotonic cache generation, bumped by clearOfflineCaches. A write that starts
// before a logout purge and lands after it would re-materialize PHI onto a
// device whose user is gone -- a literal violation of the spec §2 purge, and a
// real window now that the persist path awaits a network round-trip
// (getEncounterAssignees, or the roster's 10,000-row listPatient) between
// deciding to save and saving. Long-running writers capture the epoch first and
// drop themselves if it moved.
//
// The counter lives in localStorage, NOT in module memory. Module memory is
// per-tab: tab A's logout purge bumped only tab A's counter, so tab B's
// in-flight persist saw its own unchanged epoch and re-materialized the departed
// user's PHI after the purge -- the shared-workstation case the purge exists
// for. localStorage is synchronously shared across same-origin tabs, so simply
// READING it at check time is cross-tab correct: no BroadcastChannel, no
// `storage` listener, no async plumbing. (A listener would only buy proactive
// cancellation of an in-flight network await; it is not needed for
// correctness.)
//
// The read takes the MAX of storage and the in-memory fallback, and that is not
// belt-and-braces -- it is the only thing that keeps the guard fail-closed when
// storage is ASYMMETRICALLY broken. Safari private mode and a full quota both
// present the same way: getItem succeeds, setItem throws. Trusting the
// successful read alone meant bumpCacheEpoch swallowed its throw, storage kept
// answering the pre-purge value, and the epoch never advanced again -- silently
// disabling every purge guard in the file (both in-write checks, the callers'
// captured-epoch checks, and the read-time refusal) for the rest of the session.
// The max restores same-tab correctness. Cross-tab is unavoidably lost when
// writes fail (there is nowhere shared left to write), which is exactly the
// pre-wave behavior, not a regression.
const EPOCH_KEY = "offlineCacheEpoch.v1";
let cacheEpochFallback = 0; // private-mode / quota fallback only

export const getCacheEpoch = () => {
  let stored = 0;
  try {
    const raw = localStorage.getItem(EPOCH_KEY);
    const n = raw == null ? 0 : Number(raw);
    if (Number.isFinite(n)) stored = n;
  } catch {
    // Storage unreadable: the in-memory fallback is all there is.
  }
  return Math.max(stored, cacheEpochFallback);
};

const bumpCacheEpoch = () => {
  const next = getCacheEpoch() + 1;
  cacheEpochFallback = next;
  try {
    localStorage.setItem(EPOCH_KEY, String(next));
  } catch {
    // Private mode / quota: the in-memory fallback still guards this tab, and
    // getCacheEpoch's max() is what makes that true even though the read below
    // keeps succeeding with the stale value.
  }
  return next;
};

// THE recipe for "which cache generation and partition does this belong to",
// owned here rather than re-derived by every consumer. The ownership memo in
// useChatsOfflineHelpers kept a private copy of it; a private copy of an
// epoch+scope recipe is exactly how a cross-tenant leak gets introduced later.
export const cacheGenerationKey = (scope) => `${getCacheEpoch()}|${scope || ""}`;

// Single chokepoint for cache scope construction (review finding I3): online
// writes use the server's authData.session.currentOrganizationName (any
// case), offline reads use localStorage "organization" which login() stores
// lowercased. Normalize here so every reader/writer converges on the same
// scope string regardless of which org-name casing it started from.
export const buildScope = (userId, orgId) => {
  const org = typeof orgId === "string" ? orgId.trim().toLowerCase() : orgId;
  return userId && org ? `${userId}|${org}` : "";
};

// THE offline patient-search predicate, shared by every offline surface (the
// new-encounter picker and the patient directory) so they can't drift — and,
// more importantly, so offline search behaves like online search. The two
// hand-rolled substring filters this replaces matched only
// "firstname lastname" / email, which silently dropped both of the query
// shapes the online path supports: DOB fragments ("Smith 04/13/2000",
// "4/2000") and the "Last, First" comma form (buildPatientSearchText reorders
// it; a raw substring test against "first last" never matched).
//
// Reuses the REAL matcher: buildPatientSearchText normalizes the query exactly
// as the online lookups do, parseChatSearchTerm tokenizes it, and
// patientMatchesSearch applies the same name/DOB token rules the server-backed
// picker refines its results with. Email is kept as an extra OR: cached rows
// carry one and the online directory matches on it, but chat-shaped matching
// has no email token.
export const searchCachedPatientRows = (rows, query) => {
  const list = rows || [];
  const normalizedQuery = buildPatientSearchText(query);
  if (!normalizedQuery) return list;
  const parsed = parseChatSearchTerm(normalizedQuery);
  if (!parsed.tokens.length) return list;
  const emailNeedle = normalizedQuery.toLowerCase();
  return list.filter(
    (row) => patientMatchesSearch(row, parsed) || (row?.email || "").toLowerCase().includes(emailNeedle)
  );
};

export const isCacheFresh = (fetchedAt, maxAgeMs, now = Date.now()) =>
  typeof fetchedAt === "number" && now - fetchedAt < maxAgeMs;

// Newest-first by last-update/created, drop rows past the age window, cap
// count. The date fields are the ones REAL chat rows carry: `lastUpdate`
// (ChatList's "lastUpdate" sort reads it, may be an ISO string) and
// `createdOn` (epoch ms). Reading nonexistent `updated`/`created` keys made
// every row stamp 0, which trimmed the whole window away on save and left the
// cache permanently empty (and self-erasing).
export const trimChatWindow = (
  chats,
  { maxCount = CHAT_WINDOW_MAX_COUNT, maxAgeDays = CHAT_WINDOW_MAX_AGE_DAYS, now = Date.now() } = {}
) => {
  const cutoff = now - maxAgeDays * 24 * 60 * 60 * 1000;
  const stamp = (c) => {
    const ts = c?.lastUpdate || c?.createdOn || 0;
    const n = typeof ts === "number" ? ts : new Date(ts).getTime();
    return Number.isFinite(n) ? n : 0;
  };
  return (chats || [])
    .filter((c) => stamp(c) >= cutoff)
    .sort((a, b) => stamp(b) - stamp(a))
    .slice(0, maxCount);
};

// Replace every row for `scope` in `storeName` with `rows`, stamping each with
// {cacheKey, scope}. Also writes offlineMeta { key: `${storeName}|${scope}`,
// fetchedAt, epoch }. Row ids fall back to the array index so an id-less row can
// still round-trip (it just can't be individually addressed).
//
// The purge guard lives HERE, not only at the callers. A caller that compares
// the epoch and then calls this still awaits openLucasDb() and builds its
// transaction with no guard at all -- a purge that fully commits inside that gap
// was re-materialized. So the check runs (a) after the open await and (b) at the
// last synchronous instant before the puts, and the epoch is (c) stamped into
// the meta row so a row that survives the sub-millisecond residual window can
// still be refused at READ time.
//
// `epochAtEntry` lets a caller hand down an epoch captured before ITS OWN long
// await (the roster's multi-second listPatient, the chat window's assignee
// round-trip) instead of one captured after it.
const replaceScopeRows = async (storeName, scope, rows, fetchedAt, epochAtEntry) => {
  const epoch = epochAtEntry ?? getCacheEpoch();
  const db = await openLucasDb();
  if (!db || !scope) return false;
  if (getCacheEpoch() !== epoch) return false;
  try {
    const tx = db.transaction([storeName, "offlineMeta"], "readwrite");
    const store = tx.objectStore(storeName);
    // Delete this scope's existing rows.
    await new Promise((resolve, reject) => {
      const req = store.index("scope").openCursor(IDBKeyRange.only(scope));
      req.onsuccess = (event) => {
        const cursor = event.target.result;
        if (!cursor) return resolve();
        cursor.delete();
        cursor.continue();
      };
      req.onerror = (event) => reject(event.target?.error || event);
    });
    // Last synchronous instant before any PHI is written: if the purge landed
    // while the delete cursor was running, abandon the whole transaction.
    if (getCacheEpoch() !== epoch) {
      tx.abort();
      return false;
    }
    rows.forEach((row, i) => {
      const id = row?.id != null ? String(row.id) : `i${i}`;
      store.put({ ...row, cacheKey: `${scope}|${id}`, scope });
    });
    tx.objectStore("offlineMeta").put({ key: `${storeName}|${scope}`, fetchedAt, epoch });
    await awaitTx(tx);
    return true;
  } catch (e) {
    console.warn(`Offline cache save failed (${storeName}):`, e?.name || e?.message);
    return false;
  }
};

// A meta row stamped by a generation that has since been purged. Rows written
// BEFORE this shipped carry no epoch at all and are grandfathered in
// (`undefined` passes) -- acceptable, because a logout purge deletes them
// outright rather than relying on this check.
const isStaleEpochMeta = (meta) => meta?.epoch !== undefined && meta.epoch !== getCacheEpoch();

// Never hand a purged generation's rows to the current user; drop them on the
// way past. This is what makes the guard complete: IndexedDB has no cross-tab
// compare-and-swap, so a row can still squeak through replaceScopeRows' residual
// window -- but it carries the stale stamp and can therefore never be read back.
const refuseStaleEpoch = (storeName, scope) => {
  replaceScopeRows(storeName, scope, [], 0).catch(() => {});
  return null;
};

const loadScopeRows = async (storeName, scope) => {
  const db = await openLucasDb();
  if (!db || !scope) return null;
  try {
    const tx = db.transaction([storeName, "offlineMeta"], "readonly");
    const rows = await promisify(tx.objectStore(storeName).index("scope").getAll(IDBKeyRange.only(scope)));
    const meta = await promisify(tx.objectStore("offlineMeta").get(`${storeName}|${scope}`));
    if (!meta || !rows || rows.length === 0) return null;
    if (isStaleEpochMeta(meta)) return refuseStaleEpoch(storeName, scope);
    // Strip the cache bookkeeping fields before handing rows back.
    const clean = rows.map(({ cacheKey, scope: _s, ...rest }) => rest);
    return { rows: clean, fetchedAt: meta.fetchedAt };
  } catch (e) {
    console.warn(`Offline cache load failed (${storeName}):`, e?.name || e?.message);
    return null;
  }
};

// Freshness-only read: returns { fetchedAt } without loading the rows. The
// roster can be 10,000 patients, so a staleness check must not pull the whole
// thing into memory just to read one timestamp.
const loadScopeMeta = async (storeName, scope) => {
  const db = await openLucasDb();
  if (!db || !scope) return null;
  try {
    const tx = db.transaction([storeName, "offlineMeta"], "readonly");
    const meta = await promisify(tx.objectStore("offlineMeta").get(`${storeName}|${scope}`));
    if (!meta) return null;
    if (isStaleEpochMeta(meta)) return refuseStaleEpoch(storeName, scope);
    // Match loadScopeRows, which reports a row-less scope as no cache at all:
    // count() answers that off the index without deserializing anything.
    const count = await promisify(tx.objectStore(storeName).index("scope").count(IDBKeyRange.only(scope)));
    if (!count) return null;
    return { fetchedAt: meta.fetchedAt };
  } catch (e) {
    console.warn(`Offline cache meta load failed (${storeName}):`, e?.name || e?.message);
    return null;
  }
};

// `epoch` is the generation captured by the caller BEFORE its own network phase
// (see snapshotPatientRosterIfStale / persistChatWindowWithOwnership). Omitted,
// the write guards against the generation current at write time.
export const savePatientRoster = (scope, rows, now = Date.now(), { epoch } = {}) =>
  replaceScopeRows("patients", scope, rows, now, epoch);
export const loadPatientRosterMeta = (scope) => loadScopeMeta("patients", scope);
export const loadPatientRoster = (scope) => loadScopeRows("patients", scope);

export const saveChatList = (scope, chats, now = Date.now(), { epoch } = {}) =>
  replaceScopeRows("chats", scope, trimChatWindow(chats, { now }), now, epoch);
export const loadChatList = (scope) => loadScopeRows("chats", scope);

// Explicit-logout purge (spec §2): caches die, the recording queue does NOT —
// queued audio must survive logout (useIndexDb's never-abandon guarantee).
export const clearOfflineCaches = async () => {
  // Bump BEFORE the first await. An in-flight persist must be invalidated the
  // instant the purge is decided, not once its IDB transaction happens to
  // finish -- otherwise the race just moves. Persisted, so every other tab sees
  // it on its very next check.
  bumpCacheEpoch();
  const db = await openLucasDb();
  if (!db) return;
  try {
    const tx = db.transaction(["patients", "chats", "offlineMeta"], "readwrite");
    tx.objectStore("patients").clear();
    tx.objectStore("chats").clear();
    tx.objectStore("offlineMeta").clear();
    await awaitTx(tx);
  } catch (e) {
    console.warn("Offline cache clear failed:", e?.name || e?.message);
  }
};
